import { IconName } from '@economic/taco';
import { t } from '@lingui/macro';
import { JournalTypeEnum } from './types/internal-api';

export enum Plan {
    Internal = 'Internal',
    InternalPro = 'InternalPro',
    StartUp = 'StartUp',
    SmallBusiness = 'SmallBusiness',
    Pro = 'Pro',
    PartnerBundle = 'PartnerBundle',
    Basis = 'Basis',
    Plus = 'Plus',
    Smart = 'Smart',
    Advanced = 'Advanced',
    RgdSmall = 'RgdSmall',
    Unknown = 'Unknown',
}

export enum Entity {
    Account = 2,
    Creditor = 3,
    Debitor = 4,
    Product = 5,
    Case = 71,
    ProductGroup = 11,
}

export interface Book {
    id: number;
    name: string;
    type: number;
}

export interface LegacyDaybook {
    daybookNumber: number;
    name: string;
    entryType: EntryType;
    daybookType: JournalTypeEnum;
    nextVoucherNumber: number;
    defaultDepartment?: number;
}

export interface Quicklink {
    name: string;
    url: string;
    route: string;
}

export interface CustomTab {
    id: number;
    name: string;
    url: string;
}

export interface BasicTabMenu {
    name: string;
    routes: {
        name: string;
        route: string;
        url: string;
    }[];
}

export enum EntryType {
    All = 0,
    CustomerInvoice = 1,
    CustomerPayment = 2,
    SupplierInvoice = 3,
    SupplierPayment = 4,
    FinanceVoucher = 5,
    Reminder = 6,
    OpeningEntry = 7,
    TransferredOpeningEntry = 8,
    SystemEntry = 9,
    ManualCustomerInvoice = 10,
}

export enum EntryNumber {
    Manually = 1,
    SameNumber = 2,
    Continuous = 3,
}

export const EntryTypeToIconName: Partial<Record<EntryType, IconName>> = {
    [EntryType.CustomerInvoice]: 'entry-type-customer-invoice',
    [EntryType.CustomerPayment]: 'entry-type-customer-payment',
    [EntryType.SupplierInvoice]: 'entry-type-supplier-invoice',
    [EntryType.SupplierPayment]: 'entry-type-supplier-payment',
    [EntryType.FinanceVoucher]: 'entry-type-journal-entry',
    [EntryType.ManualCustomerInvoice]: 'entry-type-manual-customer-invoice',
    [EntryType.Reminder]: 'ledger-card-customer-reminder',
    [EntryType.OpeningEntry]: 'ledger-card-opening-entry',
};

export const EntryTypeToIconColor: Partial<Record<EntryType, string>> = {
    [EntryType.CustomerInvoice]: 'text-green-300',
    [EntryType.CustomerPayment]: 'text-green-300',
    [EntryType.SupplierInvoice]: 'text-yellow-700',
    [EntryType.SupplierPayment]: 'text-yellow-700',
    [EntryType.FinanceVoucher]: 'text-blue-300',
    [EntryType.ManualCustomerInvoice]: 'text-green-300',
    [EntryType.Reminder]: 'text-green-300',
    [EntryType.OpeningEntry]: 'text-blue-300',
};

export const EntryTypeToEntryName: Partial<Record<EntryType, string>> = {
    [EntryType.CustomerInvoice]: t`Customer invoice`,
    [EntryType.CustomerPayment]: t`Customer payment`,
    [EntryType.SupplierInvoice]: t`Supplier invoice`,
    [EntryType.SupplierPayment]: t`Supplier payment`,
    [EntryType.FinanceVoucher]: t`Finance voucher`,
    [EntryType.ManualCustomerInvoice]: t`Manual Customer Invoice`,
    [EntryType.Reminder]: t`Reminder`,
    [EntryType.OpeningEntry]: t`Opening Entry`,
};

export enum BasePaymentTypes {
    None = -1,
    Slip = -2,
    Bank = 7,
    Iban = 10,
    Entry = 14,
}

export enum SlipPaymentTypes {
    Plus71 = 1,
    Plus73 = 2,
    Plus04 = 5,
    Plus75 = 17,
}

export enum PaymentTypes {
    None = BasePaymentTypes.None,
    Plus71 = SlipPaymentTypes.Plus71,
    Plus73 = SlipPaymentTypes.Plus73,
    Plus04 = SlipPaymentTypes.Plus04,
    Bank = BasePaymentTypes.Bank,
    Iban = BasePaymentTypes.Iban,
    Entry = BasePaymentTypes.Entry,
    Plus75 = SlipPaymentTypes.Plus75,
}

export enum PaymentTermsType {
    Net = 1,
    InvoiceMonth = 2,
    DueDate = 5,
    InvoiceWeekStartingSunday = 7,
    InvoiceWeekStartingMonday = 8,
}

export enum VatZone {
    Domestic = 1,
    EU = 2,
    Abroad = 3,
    DomesticNoVat = 4,
}

// from LicenseModuleName.cs
export enum Module {
    SmallBaseLicense = 1,
    StandardBaseLicense = 2,
    Scanning = 3,
    Project = 4,
    UserAccessInvoicing = 8,
    UserAccessBookKeeping = 9,
    UserAccessReports = 10,
    UserAccessProject = 11,
    UserAccessProjectTimeLogger = 12,
    UserAccessProjectMobileTimeLogger = 13,
    Dimensions = 14,
    Inventory = 15,
    UserAccessInventory = 16,
    ClientMoneyLedger = 17,
    UserAccessProjectManager = 18,
    Subscriptions = 20,
    ImportBookedEntries = 23,
    Accruals = 27,
    Assets = 29,
    ChartofAccountsConverter = 31,
    UserBalancePosting = 33,
    ReportDesigner = 34,
    JournalPro1 = 40,
    JournalPro2 = 41,
    AutomaticAccrualInClosedPeriods = 42,
    CasewareExport = 43,
    BankIntegration = 44,
    DocumentHandling = 45,
    DocumentHandlingAdminModule = 46,
    BankIntegrationAdmin = 48,
    SieImportExport = 51,
    Mirrorcodes = 52,
    AccrualExtraEditRights = 53,
    DirectBankIntegration = 55,
    Budget = 57,
    DirectDebit = 60,
    AllClientsSignedupAreFree = 67,
    VismaWebsite = 68,
    Partnertrials = 69,
    SmartBank = 70,
    VismaWebshop = 71,
    VismaWebshopPlus = 72,
    SmartInbox = 73,
    SubscriptionsAndDirectDebit = 74,
    FixedAssetsAndAccruals = 75,
    PaymentLink = 79,
    SmartPay = 80,
}

export interface VatAccount {
    account: number;
    contraAccount?: number;
    barred: boolean;
    name: string;
    rate: number;
    rateType: number;
    rdvRate?: number;
    setupId: number;
    vatCode: string;
    vatType: number;
    vatTypeName: string;
    countryCode: string;
}

export interface FirestoreUserPreferences {
    lastSeenNotificationsTimestamp?: Date;
    table2inACBannerAcknowledged?: boolean;
    standardCOAReportBannerAcknowledged?: boolean;
    adminCentralTourHasBeenCompleted?: boolean;
    newDimensionsBannerAcknowledged?: boolean;
    dimHierarchyBannerAcknowledged?: boolean;
    dimReportBannerAcknowledged?: boolean;
}

export interface BankAccount {
    hasValue: boolean;
    name: string;
    number: number;
    bankName: string;
    setupId: number;
    bankRegistrationNumber: string;
    bankAccountNumber: string;
}

export interface BaseComboboxEntity {
    id: number;
    name: string;
}

export enum AccountTypeNumber {
    ProfitAndLoss = 1,
    Balance = 2,
    SumFrom = 3,
    Heading = 4,
    HeadingBegin = 5,
    SumInterval = 6,
    SumAlpha = 7,
}

export interface AccountType {
    canBook: boolean;
    hasBalance: boolean;
    hasValue: boolean;
    name: string;
    number: AccountTypeNumber;
    textId: string;
}

export interface SelectableAccount {
    name: string;
    number: number;
    type: AccountType;
}

// Suffixed "Model" because there is a DOM APIs Account interface
export interface AccountModel {
    access: boolean;
    balance: number;
    isBlockedForDirectEntries: boolean;
    isDefaultCredit: boolean;
    isDepartmentMandatory: boolean;
    isUnitMandatory: boolean;
    name: string;
    number: number;
    type: AccountType;
}

export interface LicenseAccess {
    licenseModuleId: number;
    agreementId?: number;
    id?: number;
    unsubscriptionDate?: Date;
    subscriptionDate: Date;
    discount: number;
    shouldBeCredited: boolean;
    isPartOfPlan: boolean;
    discountExpiryDate?: Date;
    hasValue: boolean;
}

export enum SourceComponent {
    ComboBox,
    ACTable,
}

export enum AdministratorSegmentType {
    // Admin type is Group of Companies + Admin group is one of Solutionpartners, Integrationspartner, Premium Vertikaler/Apps
    Partner = 1,
    // Admin type is Group of Companies + Admin group is NOT one of Solutionpartners, Integrationspartner, Premium Vertikaler/Apps
    GroupOfCompanies = 2,
    // Admin type is NOT Group of Companies
    AdminOffice = 3,
}

export enum AdministratorType {
    Chartered = 1,
    Certified = 2,
    Other = 3,
    AdminOffice = 4,
    Concern = 5,
}
